import type { ApiErrorDetail, LocalizedText } from '@dob/domain';
import { fieldIssueMessage, type FieldIssueCode, type IssueParams } from '@dob/domain/validation';

import { ApiError } from './api/client.ts';

/**
 * Field errors for forms (owner feedback after M6a, D93): every refused field shows its exact
 * reason under the field itself, in the person's language, and the field is highlighted.
 * Client checks use the same messages as the server, so the words never differ.
 */
export type ClientErrors = Partial<Record<string, LocalizedText>>;

/** Server error codes that belong to one field (optionally with clearer words for that form). */
export type CodeFields = Readonly<
  Record<string, string | { readonly field: string; readonly message: LocalizedText }>
>;

export const issue = (code: FieldIssueCode, params?: IssueParams): LocalizedText =>
  fieldIssueMessage(code, params);

/** The first server detail for each field path, plus errors that belong to one field by code. */
export function serverFieldErrors(
  error: unknown,
  codeFields: CodeFields = {},
): Partial<Record<string, LocalizedText>> {
  const byField: Partial<Record<string, LocalizedText>> = {};
  if (!(error instanceof ApiError)) return byField;
  const target = codeFields[error.code];
  if (typeof target === 'string' && error.serverMessage) byField[target] = error.serverMessage;
  else if (target && typeof target === 'object') byField[target.field] = target.message;
  for (const detail of details(error)) byField[detail.path] ??= detail.message;
  return byField;
}

export function details(error: ApiError): readonly ApiErrorDetail[] {
  if (!Array.isArray(error.details)) return [];
  return (error.details as unknown[]).filter(
    (d): d is ApiErrorDetail =>
      typeof d === 'object' &&
      d !== null &&
      typeof (d as ApiErrorDetail).path === 'string' &&
      typeof (d as ApiErrorDetail).message === 'object',
  );
}

/**
 * Whether an error still needs the alert above the form: not when everything it says is already
 * shown under the fields.
 */
export function needsAlert(
  error: unknown,
  fields: readonly string[],
  codeFields: CodeFields = {},
): boolean {
  if (!error) return false;
  if (!(error instanceof ApiError)) return true;
  if (codeFields[error.code]) return false;
  // Any refusal (validation, "already taken"…) whose every detail sits under a field needs no alert.
  const list = details(error);
  return list.length === 0 || list.some((d) => !fields.includes(d.path));
}

// ── Client checks (same rules and words as the server) ──────────────────────

export function checkRequired(value: string): LocalizedText | undefined {
  return value.trim() === '' ? issue('required') : undefined;
}

export function checkLength(value: string, min: number, max?: number): LocalizedText | undefined {
  const length = Array.from(value.trim()).length;
  if (length === 0) return issue('required');
  if (length < min) return issue('too_short', { min });
  if (max !== undefined && length > max) return issue('too_long', { max });
  return undefined;
}

export function checkEmail(value: string): LocalizedText | undefined {
  if (value.trim() === '') return undefined;
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim()) ? undefined : issue('invalid_email');
}

export function checkCode(value: string): LocalizedText | undefined {
  return /^\d{6}$/.test(value) ? undefined : issue('code_six_digits');
}

/** Only the entries that failed; empty means the form may be sent. */
export function collect(checks: Record<string, LocalizedText | undefined>): ClientErrors {
  const errors: ClientErrors = {};
  for (const [field, message] of Object.entries(checks)) if (message) errors[field] = message;
  return errors;
}

export function hasErrors(errors: ClientErrors): boolean {
  return Object.keys(errors).length > 0;
}
